/*  iceberg-ui  */
import { Flex, Icon, IconWithVariant } from '@auviknetworks/iceberg-ui';

/*  MUI  */
import { Alert, Grid, IconButton, Typography } from '@mui/material';

/*  Shared  */
import {
    LifecyclePicker,
    RiskPicker
} from 'application/Shared/Components/Pickers';
import { TagLinksList } from 'application/Shared/Components/Tag';
import { FontSizes } from 'application/Shared/Constants';
import { Helpers } from 'application/Shared/Helpers';
import { EditTagLinksModal } from 'application/Shared/Modals/Application/globalTag/EditTagLinksModal';
import { Application as ApplicationType } from 'application/Shared/types/application';
import { TenantApp } from 'application/Shared/types/tenantApp';

/* Hooks */
import { useFetchTagLinks } from 'hooks/dataAccess/GlobalTag';

/* Objects  */
import {
    Application,
    ApplicationBusinessOwner,
    ApplicationCategoryAndSubCategory
} from 'objects/Application';
import Owner from 'objects/functions/BusinessOwner';

/*  React  */
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

/* Internal Components */
import { BusinessOwnerReturnValue, InlineEdit } from './AppDetailsInlineEdit';
import { ContractRouteWrapper } from './ContractRouteWrapper';

/**
 * Component for displaying and managing application details settings
 * @param {Object} props - Component props
 * @param {ApplicationType} props.application - The application object containing core application data
 * @param {Function} props.handleModalViewTabChange - Callback function to handle modal tab changes
 * @param {boolean} props.modalView - Flag indicating if component is rendered in modal view
 * @param {Function} props.reloadApplication - Callback function to reload application data
 * @param {TenantApp} props.tenantApp - The tenant application object containing tenant-specific data
 */
export const AppDetailsSettings = ({
    application,
    handleModalViewTabChange,
    modalView,
    reloadApplication,
    tenantApp
}: {
    application: ApplicationType;
    handleModalViewTabChange?: (
        _e: React.SyntheticEvent,
        value: string
    ) => void;
    modalView?: boolean;
    reloadApplication: () => void;
    tenantApp: TenantApp;
}) => {
    const { t } = useTranslation();
    const { clientId } = useParams();
    const [openTagsModal, setOpenTagsModal] = useState(false);

    const canEdit = tenantApp.classified;

    const params = useMemo(
        () => ({
            applicationId: application.id,
            includeTag: true,
            includeTagSettings: true,
            mergeClientAndPartner: true,
            platformTenantId: clientId,
            size: -1
        }),
        [application.id, clientId]
    );

    const {
        tagLinks,
        loading: tagLinksLoading,
        refetchData: refetchTagLinks
    } = useFetchTagLinks(params);

    const { risk, businessOwnerPlatformUserId } = tenantApp;

    if (
        !application.businessOwnerPlatformUserId &&
        businessOwnerPlatformUserId
    ) {
        // eslint-disable-next-line no-param-reassign
        application.businessOwnerPlatformUserId = businessOwnerPlatformUserId;
    }

    const contractSet = Helpers.isset(tenantApp, 'contracts', 0);

    // tenantApp.description is the description at the client level
    // application.description is the app default at the partner level.
    const appDescription = tenantApp.description || application.description;

    return (
        <Application data={application}>
            <Grid container spacing={1}>
                {!canEdit && (
                    <Grid item xs={12} paddingBottom="16px">
                        <Alert
                            severity="info"
                            icon={false}
                            sx={{ justifyContent: 'center' }}
                        >
                            {t(
                                'ApplicationDashboard.details.needToClassifyToEdit'
                            )}
                        </Alert>
                    </Grid>
                )}
                <Grid item xs={12} md={6}>
                    <InlineEdit
                        canEdit={canEdit}
                        currentValue={tenantApp.categoryId}
                        displayValue={
                            <ApplicationCategoryAndSubCategory
                                noTruncate
                                size="normal"
                            />
                        }
                        field="categoryId"
                        header={t('Common.category')}
                        reloadApplication={reloadApplication}
                        tenantApp={tenantApp}
                    />
                    <InlineEdit
                        canEdit={canEdit}
                        currentValue={risk}
                        displayValue={<RiskPicker visualOnly value={risk} />}
                        field="risk"
                        header={t('Common.risk')}
                        reloadApplication={reloadApplication}
                        tenantApp={tenantApp}
                    />
                    <InlineEdit
                        canEdit={canEdit}
                        currentValue={tenantApp.status}
                        displayValue={
                            <LifecyclePicker
                                visualOnly
                                value={tenantApp.status}
                            />
                        }
                        field="status"
                        header={t('Common.lifecycleStage')}
                        reloadApplication={reloadApplication}
                        tenantApp={tenantApp}
                    />
                    <InlineEdit
                        canEdit={canEdit}
                        displayValue={
                            <ApplicationBusinessOwner
                                // @ts-expect-error Component is not typed
                                noBusinessOwnerRequired={
                                    tenantApp.noBusinessOwnerRequired
                                }
                                managedByPartner={tenantApp.managedByPartner}
                                managed3rdParty={tenantApp.managed3rdParty}
                            />
                        }
                        field="businessOwner"
                        handleDataChange={
                            Owner.handleValues as (
                                value: string
                            ) => BusinessOwnerReturnValue
                        }
                        header={t('Common.business_owner')}
                        reloadApplication={reloadApplication}
                        tenantApp={tenantApp}
                    />
                    <>
                        <Flex sx={{ alignItems: 'center' }}>
                            <Typography color="textSecondary" fontWeight={600}>
                                {t('Common.tags')}
                            </Typography>
                            {canEdit && (
                                <IconButton
                                    edge="end"
                                    size="small"
                                    color="secondary"
                                    disableFocusRipple
                                    onClick={() => setOpenTagsModal(true)}
                                >
                                    <Icon src="Pencil" size={FontSizes[2]} />
                                </IconButton>
                            )}
                        </Flex>
                        {!tagLinksLoading && (
                            // @ts-expect-error Component is not typed
                            <TagLinksList tagLinks={tagLinks} />
                        )}
                        {openTagsModal && (
                            <EditTagLinksModal
                                applicationId={application.id}
                                currentTagLinks={tagLinks}
                                open={openTagsModal}
                                onClose={() => {
                                    refetchTagLinks();
                                    setOpenTagsModal(false);
                                }}
                            />
                        )}
                    </>
                </Grid>
                <Grid item xs={12} md={6}>
                    <InlineEdit
                        canEdit={canEdit}
                        currentValue={appDescription}
                        displayValue={appDescription || t('Common.noData')}
                        field="description"
                        header={t('Common.description')}
                        multiline
                        reloadApplication={reloadApplication}
                        tenantApp={tenantApp}
                    />
                    <InlineEdit
                        canEdit={canEdit}
                        currentValue={tenantApp.purpose}
                        displayValue={
                            tenantApp.purpose
                                ? tenantApp.purpose
                                : t('Common.business_purpose_not_defined')
                        }
                        field="purpose"
                        header={t('Common.business_purpose')}
                        multiline
                        reloadApplication={reloadApplication}
                        tenantApp={tenantApp}
                    />
                    <>
                        <Flex sx={{ alignItems: 'center' }}>
                            <Typography color="textSecondary" fontWeight={600}>
                                {t('Common.contract_configured')}
                            </Typography>
                            {canEdit && (
                                <ContractRouteWrapper
                                    handleModalViewTabChange={
                                        handleModalViewTabChange
                                    }
                                    modalView={modalView}
                                >
                                    <IconButton
                                        edge="end"
                                        size="small"
                                        color="secondary"
                                        disableFocusRipple
                                    >
                                        <Icon
                                            src="Pencil"
                                            size={FontSizes[2]}
                                        />
                                    </IconButton>
                                </ContractRouteWrapper>
                            )}
                        </Flex>
                        {contractSet ? (
                            <Flex sx={{ alignItems: 'center' }}>
                                <IconWithVariant
                                    variant="success"
                                    src="CheckCircle"
                                    size={FontSizes[3]}
                                    wrapperProps={{ marginRight: '2px' }}
                                />
                                <Typography variant="span">
                                    {t('Common.contract_hasBeenConfigured')}
                                </Typography>
                            </Flex>
                        ) : (
                            <Flex sx={{ alignItems: 'center' }}>
                                <IconWithVariant
                                    variant="danger"
                                    src="CloseCircle"
                                    size={FontSizes[3]}
                                    wrapperProps={{ marginRight: '2px' }}
                                />
                                <Typography variant="span">
                                    {t('Common.contract_hasNotBeenConfigured')}
                                </Typography>
                            </Flex>
                        )}
                    </>
                </Grid>
            </Grid>
        </Application>
    );
};
